/* „Materiały" — KANON `Cwiczenia` 17.09.2026, `2-karta.html#e`'s `materialsBlock()` and `MB.linkRow`.
 *
 * A heading with the „n z 3" count beside it, the sentence that says whose links these are, the
 * rows, and a footer of text buttons. The sentence's box and the footer's row both hold their
 * height from the first frame: the overlay read that decides what either of them says lands after
 * the rows do, and a block that grew when it answered would push „Jak wykonać" down the rail.
 *
 * Every text leaf states its line-height; the count is tabular. */

.block {
  display: block;
}

.head {
  display: flex;
  gap: var(--space-3);
  align-items: baseline;
  margin-bottom: var(--space-10px);
}

/* NOT `flex: 1`. The canon sets „2 z 3" directly beside the heading on its own baseline, not at the
 * far right of the rail — the count is a fact about the word next to it, and a rail's width away it
 * reads as a column header for something else. */
.title {
  margin: var(--space-0);
  font-size: var(--fs-body);
  font-weight: var(--fw-bold);
  line-height: var(--lh-display);
  letter-spacing: var(--ls-heading);
  color: var(--text);
}

/* It takes focus back from the revert prompt and never from a Tab, so it carries no visible ring of
 * its own — a heading that outlined on click would be a control the trainer cannot press. */
.title:focus {
  outline: none;
}

.count {
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  line-height: var(--lh-display);
  color: var(--text-3);
  font-variant-numeric: tabular-nums;
}

/* RESERVED. Two lines of the note's own leading, whether or not the overlay read has answered —
 * `exerciseLibrary.links.inheritedNote` is the longest of the three and wraps to two at this width. */
.note {
  min-height: var(--h-ex-theatre-note);
  margin: var(--space-0) var(--space-0) var(--space-10px);
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
  line-height: var(--lh-tight);
  color: var(--text-3);
}

.rows {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.empty {
  margin: var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-3);
}

/* ── One saved material ─────────────────────────────────────────────────────────────────────── */

.row {
  display: grid;
  grid-template-columns: var(--h-ex-theatre-link) 1fr 14px;
  gap: var(--space-3);
  align-items: center;
  padding: var(--space-10px) var(--space-3);
  color: inherit;
  text-decoration: none;
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-sm);
}

.row:hover {
  border-color: var(--border-2);
}

.row:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* The plate the brand mark stands on. LIGHT in both themes: a multi-colour logo on a near-black
 * ground loses its dark strokes, which is why `LinkBrandMarks` says the plate is the caller's and
 * why every mark in the canon sits on one. The neutral chain glyph is not a logo and takes the ink
 * around it, so it reads on the same plate either way. */
.mark {
  display: grid;
  place-items: center;
  width: var(--h-ex-theatre-link);
  height: var(--h-ex-theatre-link);
  color: var(--text-2);
  background: var(--plate-on-photo);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-8px);
}

.markGlyph {
  display: block;
  width: 17px;
  height: 17px;
}

.rowText {
  min-width: 0;
}

.rowLabel {
  display: block;
  overflow: hidden;
  font-size: var(--fs-exercise);
  font-weight: var(--fw-bold);
  line-height: var(--lh-display);
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rowHost {
  display: block;
  overflow: hidden;
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
  line-height: var(--lh-display);
  color: var(--text-3);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rowOut {
  display: block;
  width: 14px;
  height: 14px;
  color: var(--text-3);
}

/* ── The editor ─────────────────────────────────────────────────────────────────────────────── */

.editorRow {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: var(--space-2);
  align-items: start;
}

.fields {
  display: flex;
  flex-direction: column;
  gap: var(--space-5px);
  min-width: 0;
}

.field {
  width: 100%;
  min-width: 0;
  height: var(--h-pl-control);
  padding: var(--space-0) var(--space-9-5px);
  font-family: var(--font-body);
  font-size: var(--fs-pl-control);
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-8px);
}

.field::placeholder {
  color: var(--text-3);
}

/* INSET, like the rail's own ring: the rail scrolls with no inset on its left, so an outset ring
 * on a field flush against that edge was clipped by the scroller's `overflow` and read as cut. */
.field:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--outline-inset);
}

/* The refused field carries the danger edge as well as the sentence under it. Never colour ALONE —
 * the words below say what is wrong and are wired as the field's description. */
.fieldRefused {
  border-color: var(--danger);
}

.remove {
  display: grid;
  place-items: center;
  width: var(--h-pl-control);
  height: var(--h-pl-control);
  padding: var(--space-0);
  line-height: var(--lh-none);
  color: var(--text-2);
  cursor: pointer;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-8px);
}

.remove:hover {
  color: var(--text);
  border-color: var(--text-2);
}

.remove:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--outline-inset);
}

.removeGlyph {
  display: block;
  width: 15px;
  height: 15px;
}

/* Spans both columns, so a long sentence runs the full width of the row rather than under the
 * fields alone. */
.refusal {
  grid-column: 1 / -1;
  margin: var(--space-0);
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-tight);
  color: var(--danger);
}

/* RESERVED and always drawn, empty when there is nothing to say. A `role="alert"` inserted with
 * its text is a region that did not exist when the failure happened, and a line that appears pushes
 * the whole footer down inside a rail the trainer is reading. `min-height` rather than a fixed one:
 * a long refusal wraps and grows downward, which is the direction nothing is standing in. */
.saveError {
  min-height: calc(var(--fs-label) * var(--lh-tight));
  margin: var(--space-10px) var(--space-0) var(--space-0);
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-tight);
  color: var(--danger);
}

/* ── The footer ─────────────────────────────────────────────────────────────────────────────── */

/* RESERVED, and always drawn: „Przywróć wersję z biblioteki" arrives with the overlay read, and a
 * row that grew when it did would push the whole rail below it.
 *
 * The text buttons inside it are NEUTRAL: the accent appears nowhere on this card, and the specimen
 * writes its own `.mb-text-btn` in `--text-2`. `--inline-text-ink` is the shared treatment's own
 * hook — see `shared/ui/inlineTextButton.module.css`. */
.foot {
  --inline-text-ink: var(--text-2);

  display: flex;
  flex-wrap: wrap;
  gap: var(--space-14px);
  align-items: center;
  min-height: var(--h-ex-theatre-foot);
  margin-top: var(--space-10px);
}

/* Why the save will not act, and the button's own `aria-describedby`. It takes the footer's whole
 * first line so the two answers and the three controls never fight for the same row, and the line
 * is held whether or not there is anything to say — see `.saveError` above. */
.saveNote {
  flex: 1 0 100%;
  min-height: calc(var(--fs-note) * var(--lh-tight));
  margin: var(--space-0);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-tight);
  color: var(--text-2);
}

.spacer {
  flex: 1;
}

/* GEOMETRY for `shared/ui/ghostButton` — the treatment is that file's, and nothing is on both
 * sides. */
.footButton {
  height: var(--h-detail-btn);
  padding: var(--space-0) var(--space-3);
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  background: var(--bg-card);
  border-radius: var(--radius-8px);
}

/* The save is the INK button's fill on a ghost's geometry: this card has no ember on it at all —
 * the accent is a scalpel and a materials save is not the screen's one real action — but the answer
 * that writes still has to read louder than the one that walks away.
 *
 * TWO classes, so specificity decides rather than the bundler. `.footButton` above declares
 * `background` (it is geometry for a ghost, per that treatment's own contract) and so does this, and
 * two single-class rules writing one property are settled by chunk order — which differs between the
 * dev server and the build. It lost there once, and the save rendered as a second Anuluj. */
.footButton.footSave {
  color: var(--bg);
  background: var(--text);
  border-color: var(--text);
}

.footButton.footSave[aria-disabled='true'] {
  color: var(--text-2);
  background: var(--bg-chip);
  border-color: var(--border);
  cursor: not-allowed;
}
